import { useCallback } from 'react'
import * as React from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import Accordion from 'LuxKit/Accordion'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import Divider from 'LuxKit/Divider'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import MarkdownRender from 'LuxKit/MarkdownRender'
import ExperienceDetailsAboutLocationMap from 'components/Experiences/ExperienceDetailsModal/About/ExperienceDetailsAboutLocationMap'
import TextArea from 'components/Common/Form/Input/TextArea'
import Pane from 'components/Common/Pane'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import useToggle from 'hooks/useToggle'
import styled from 'styled-components'
import { rem } from 'polished'
import type { LoyaltyTransferFormData } from './checkoutLoyaltyTransferTypes'

const Section = styled(Pane)`
  padding: ${rem(12)} ${rem(16)};
  border-radius: ${props => props.theme.borderRadius.L};
`

const SpecialRequestTextArea = styled(TextArea)`
  max-width: ${rem(600)};
`

interface Props {
  form: UseFormReturn<LoyaltyTransferFormData>
  title: string
  subtitle: string
  selectedTransferOption?: App.ExperienceTransferOption
  oneClickTransfer: App.ExperienceBookingView
  tier: App.LoyaltyTier
  pointsEarnCalculationRequests?: Array<App.LoyaltyPointsEarnCalculationRequest | undefined>
  paidReturnTransferPricing?: {
    totalPrice: number
    freePrice: number
    paidPrice: number
    freeTransferOption: App.ExperienceTransferOption
    paidTransferOption: App.ExperienceTransferOption
    freeDirection: string
    paidDirection: string
  }
  currentTransferView: any // Add this for the unified transfer view
  onBack?: () => void
  onSubmit: () => void
  onRemove?: () => void
}

function CheckoutLoyaltyTransferDetailsStep(props: Props) {
  const {
    form,
    title,
    subtitle,
    selectedTransferOption,
    oneClickTransfer,
    tier,
    pointsEarnCalculationRequests,
    paidReturnTransferPricing,
    currentTransferView,
    onBack,
    onSubmit,
    onRemove,
  } = props

  const intl = useIntl()
  const { watch, setValue } = form
  const specialRequests = watch('specialRequests')
  const transferType = watch('transferType')
  const specialRequestsToggle = useToggle(false)
  const experience = oneClickTransfer?.originalExperience

  const handleSpecialRequestsChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue('specialRequests', e.target.value)
  }, [setValue])

  const hasDescription = !!experience?.copy?.dealDescription

  return (
    <>
      <ModalHeader
        title={title}
        onBackButtonClick={currentTransferView.isEditing ? undefined : onBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={20}>
            {selectedTransferOption && (
              <Section background={`tier-${tier}-primary`}>
                <Group direction="horizontal" gap={8}>
                  <BodyText variant="medium" weight="bold">
                    <FormattedMessage
                      id="1XP74WE0DjLxjx32Y_QZ"
                      defaultMessage="Included:"
                      description="Label prefix for the included travellers and bags in loyalty transfer details"
                    />
                  </BodyText>
                  <BodyTextBlock variant="medium" startIcon={<LineUsersAltIcon />}>
                    <FormattedMessage
                      id="s5XjEkvLT19Kt-ARSTci"
                      defaultMessage="{count} travellers"
                      description="Number of travellers included in the loyalty airport transfer option"
                      values={{ count: selectedTransferOption.maxPassengers }}
                    />
                  </BodyTextBlock>
                  <BodyTextBlock variant="medium" startIcon={<LineBagIcon />}>
                    <FormattedMessage
                      id="g6U5ALSNQMl0XwNveshn"
                      defaultMessage="{count} bags"
                      description="Number of bags included in the loyalty airport transfer option"
                      values={{ count: selectedTransferOption.maxCheckedBags }}
                    />
                  </BodyTextBlock>
                </Group>
              </Section>
            )}

            <VerticalSpacer gap={16}>
              <Heading variant="heading4">{subtitle}</Heading>
              {hasDescription && (
                <LargeContentToggle
                  maxHeight={80}
                  showMoreText={intl.formatMessage({
                    id: 'G6oBzs64nd8lbHVGxPnm',
                    defaultMessage: 'Read more',
                    description: 'Toggle button to expand the transfer deal description in loyalty details step',
                  })}
                  showLessText={intl.formatMessage({
                    id: 'cwaCizNX-oYkd7OkLPDW',
                    defaultMessage: 'Read less',
                    description: 'Toggle button to collapse the transfer deal description in loyalty details step',
                  })}
                >
                  <MarkdownRender content={experience?.copy?.dealDescription ?? ''} />
                </LargeContentToggle>
              )}
              <Divider kind="primary" />
              {experience?.copy?.included?.trim() && (
                <>
                  <div>
                    <LargeContentToggle
                      maxHeight={80}
                      showMoreText={intl.formatMessage({
                        id: 'ToZm51hxYVp62qTMRssk',
                        defaultMessage: 'View more inclusions',
                        description: 'Toggle button to expand and show more transfer inclusions in loyalty step',
                      })}
                      showLessText={intl.formatMessage({
                        id: 'C1FSlDa-e9CSDXw_XLne',
                        defaultMessage: 'View less inclusions',
                        description: 'Toggle button to collapse and hide transfer inclusions in loyalty step',
                      })}
                    >
                      <BodyText as="h6" variant="large" weight="bold">
                        <FormattedMessage
                          id="SmLZ_ZWTJDeR1KlYd-MX"
                          defaultMessage="Inclusions"
                          description="Section heading for the list of inclusions in the loyalty transfer details step"
                        />
                      </BodyText>
                      <MarkdownRender type="compact" content={experience.copy.included} />
                    </LargeContentToggle>
                  </div>
                  <Divider kind="primary" />
                </>
              )}
            </VerticalSpacer>

            <Divider direction="vertical" kind="secondary" />

            <div>
              <Accordion
                heading={intl.formatMessage({
                  id: 'ECzo0klSU1leD4WpAEt6',
                  defaultMessage: 'Location',
                  description: 'Accordion heading for the location map section in loyalty transfer details',
                })}
                size="large"
                hideDivider
              >
                <ExperienceDetailsAboutLocationMap experienceView={oneClickTransfer} />
              </Accordion>
              {experience?.copy?.thingsToKnow?.map(item => (
                <Accordion
                  key={item.title}
                  heading={item.title}
                  size="large"
                  hideDivider
                >
                  <MarkdownRender content={item.content} type="compact" />
                </Accordion>
              ))}
            </div>

            <TextLink
              size="medium"
              weight="regular"
              endIcon={<DropdownChevron open={specialRequestsToggle.value} />}
              onClick={specialRequestsToggle.toggle}
            >
              <FormattedMessage
                id="vYofMhYpuGUKwu3uoxn-"
                defaultMessage="Add special requests (optional)"
                description="Toggle link to show the optional special requests text area for loyalty transfer"
              />
            </TextLink>
            {specialRequestsToggle.value && (
              <SpecialRequestTextArea
                resize="vertical"
                value={specialRequests}
                onChange={handleSpecialRequestsChange}
                helpText={intl.formatMessage({
                  id: 'PAuhhuh68dUYIcVYPJ6_',
                  defaultMessage: 'Your requests will be passed to the vendor. Requests are not guaranteed.',
                  description: 'Help text below the special requests field explaining requests are not guaranteed',
                })}
              />
            )}
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <Group direction="horizontal" gap={8} horizontalAlign="space-between">
          <div>
            {transferType === 'PAID_RETURN_TRANSFER' && selectedTransferOption && paidReturnTransferPricing && (
              <Group direction="vertical" gap={4} verticalAlign="center">
                <PriceRowPrice saleUnit="total" size="S" price={selectedTransferOption.price} />
                <LoyaltyPointsEarn
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType="exact"
                />
              </Group>
            )}
          </div>
          <Group direction="horizontal" gap={8}>
            {currentTransferView.isEditing && (
              <TextButton kind="secondary" onClick={onRemove}>
                <FormattedMessage
                  id="jrjCy02l8WTCxE-7-qYm"
                  defaultMessage="Remove"
                  description="Button to remove the loyalty airport transfer from the checkout booking"
                />
              </TextButton>
            )}
            {!currentTransferView.isEditing && <TextButton kind="primary" onClick={onSubmit}>
              <FormattedMessage
                id="Lb4Y40d05v0zTmqwFYUq"
                defaultMessage="Add to booking"
                description="Button to add the loyalty airport transfer to the checkout booking order"
              />
            </TextButton>}
            {currentTransferView.isEditing && <TextButton kind="primary" onClick={onSubmit}>
              <FormattedMessage
                id="FZXAZz8jsDfsxe0UrxLb"
                defaultMessage="Update"
                description="Button to save changes to an existing loyalty airport transfer in checkout"
              />
            </TextButton>}
          </Group>
        </Group>
      </ModalFooter>
    </>
  )
}

export default CheckoutLoyaltyTransferDetailsStep
